/*
Author: Jude Fernando
Date: 25/09/2024
Copyright © 2024 Infix!. All rights reserved.
*/

import InputField from "@/components/Atoms/InputField";
import InputLabel from "@/components/Atoms/InputLabel";
import { useResponseId, useUrlType } from "@/components/hooks";
import { RootState } from "@/reducers/store";
import { useValidate } from "@/state/survey/formSlice";
import { BasicAnswerSave, useUpdateAnswersSubmit } from "@/state/survey/saveAnswerSlice";
import { Utility, Validate } from "@/utils";
import { ReactNode, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { INode } from "../SurveyModel/types/nodeTypes";
import { DECIMAL_3_DIGITS_1_DP_REGEX, TWO_DIGIT_REGEX } from "@/constants/constants";

interface IWeightInput {
  node: INode;
  children?: ReactNode;
  name: string;
  disabled: boolean;
  repeatedElement?: boolean;
  onBlur?: () => void;
  type?: "Number";
}

const WeightInput: React.FC<IWeightInput> = (props: IWeightInput) => {
  const { node, name, disabled, repeatedElement, onBlur } = props;
  const form = useSelector((state: RootState) => state.form);
  const validateInputField = useValidate();
  const updateAnswersDispatch = useUpdateAnswersSubmit();
  const { answerResponseId } = useResponseId();
  const { state = {} } = form[node.name] || {};
  const { isPatient } = useUrlType();

  const getInitialWeightValue = (): number | undefined => {
    const formValue = form[node.name]?.value;
    return typeof formValue === "number" ? formValue : undefined;
  };

  const [weight, setWeight] = useState<number | undefined>(getInitialWeightValue());
  const [unit, setUnit] = useState<"metric" | "imperial">(node.unit || "metric");

  const convertWeightToImperial = (
    kg?: number
  ): { stone?: number; pounds?: number } => {
    if (kg === undefined || kg === null) return { stone: undefined, pounds: undefined };
    const totalPounds = kg / 0.453592;
    const stone = Math.floor(totalPounds / 14);
    let pounds = Math.round(totalPounds % 14);

    if (pounds === 14) {
      return { stone: stone + 1, pounds: 0 };
    }

    return { stone, pounds };
  };

  const { stone, pounds } = convertWeightToImperial(weight);

  useEffect(() => {
    const formValue = form[node.name]?.value;
    if (typeof formValue === "number") {
      setWeight(formValue);
    } else {
      setWeight(undefined);
    }
  }, [form, node.name]);

  const handleUnitSwitch = () => {
    if (unit === 'metric') {
        setUnit('imperial');
      } else {
        if (weight !== undefined) {
          setWeight(Number(weight.toFixed(1)));
        }
        setUnit('metric');
      }
    validateInputField(name, weight, true);
  };

  const handleOnBlur = () => {
    if (!isPatient && answerResponseId && !repeatedElement && weight !== undefined) {
      const answers: BasicAnswerSave[] = [{
        name: node.name,
        value: Number(weight.toFixed(1)),
      }];
      updateAnswersDispatch(answers, answerResponseId);
    }
    if (repeatedElement && onBlur) onBlur();
  };

  const handleWeightKgChange = (value: string) => {
    if (!DECIMAL_3_DIGITS_1_DP_REGEX.test(value) && value !== '') return;

    let newWeight: number | undefined;
    if (value === '') {
      newWeight = 0;
    } else {
      newWeight = Number(value);
    }
    setWeight(newWeight);
    validateInputField(name, newWeight, true);
  };

  const handleWeightStChange = (value: string) => {
    if (!TWO_DIGIT_REGEX.test(value) && value !== '') return;

    const stoneInput = Number(value || 0);
    const totalPounds = (stoneInput * 14) + (pounds || 0);
    const kg = totalPounds / 2.20462;
    const newWeight = Number(kg.toFixed(1));
    setWeight(newWeight);
    validateInputField(name, newWeight, true);
  };

  const handleWeightLbChange = (value: string) => {
    if (!TWO_DIGIT_REGEX.test(value) && value !== '') return;
    
    const lbInput = Number(value || 0);
    const totalPounds = ((stone || 0) * 14) + lbInput;
    const kg = totalPounds / 2.20462;
    const newWeight = Number(kg.toFixed(1));
    setWeight(newWeight);
    validateInputField(name, newWeight, true);
  };

  const validateKG = (value: string) => {
    if (!DECIMAL_3_DIGITS_1_DP_REGEX.test(value) && value !== '') return false;
    return true;
  };

  const validateStone = (value: string) => {
    if (!TWO_DIGIT_REGEX.test(value) && value !== '') return false;
    return true;
  };

  const validatePounds = (value: string) => {
    if (Number(value) >= 14) return false;
    return true;
  };

  return (
    <div
      className={`height-weight-input-wrap 
        ${Utility.getClassForWidthPx(node)} 
        ${node.name}
        ${Utility.getClassForWidth(node)} 
        ${
          !Utility.getClassForWidthPx(node) && !Utility.getClassForWidth(node)
            ? "flex-1"
            : ""
        } ${
          !repeatedElement
            ? `user-input ${Utility.getClassForWidth(node)} 
        question-bottom-space-1
         input-border-top`
            : ""
        }`}
    >
      <div className={`${Utility.getClassForWidth(node)}`}>
        <InputLabel label={node.title} labelClasses="input-label"></InputLabel>
        {node.subTitle ? (
          <InputLabel
            type="subTitle"
            label={node.subTitle}
            labelClasses="input-label"
          ></InputLabel>
        ) : (
          ""
        )}

        {unit === "metric" ? (
          <div>
            <div className="metric-input-wrap">
              <InputLabel
                type="subTitle"
                label="Kilograms"
                labelClasses="input-label"
              ></InputLabel>
              <InputField
                disabled={disabled}
                form={form}
                isInValid={Validate.isInvalid(state)}
                propClasses={"height-weight-input-text flex"}
                value={weight?.toString() || ""}
                name={`${name}_kg`}
                onChange={handleWeightKgChange}
                onBlur={handleOnBlur}
                type="Number"
                decimalPlaces={1}
                validate={validateKG}
              />
            </div>
            <button
              type="button"
              className="unit-switch-link"
              onClick={handleUnitSwitch}
            >
              Switch to {unit === "metric" ? "imperial" : "metric"}
            </button>
          </div>
        ) : (
          <div>
            <div className="imperial-wrap">
              <div className="imperial-input-wrap">
                <InputLabel
                  type="subTitle"
                  label="Stone"
                  labelClasses="input-label"
                ></InputLabel>
                <InputField
                  disabled={disabled}
                  form={form}
                  isInValid={Validate.isInvalid(state)}
                  propClasses={"height-weight-input-text flex"}
                  value={stone?.toString() || ""}
                  name={`${name}_st`}
                  onChange={handleWeightStChange}
                  onBlur={handleOnBlur}
                  type="Number"
                  validate={validateStone}
                />
              </div>
              <div className="imperial-input-wrap">
                <InputLabel
                  type="subTitle"
                  label="Pounds"
                  labelClasses="input-label"
                ></InputLabel>
                <InputField
                  disabled={disabled}
                  form={form}
                  isInValid={Validate.isInvalid(state)}
                  propClasses={"height-weight-input-text flex"}
                  value={pounds?.toString() || ""}
                  name={`${name}_lb`}
                  onChange={handleWeightLbChange}
                  onBlur={handleOnBlur}
                  type="Number"
                  validate={validatePounds}
                />
              </div>
            </div>
            <button
              type="button"
              className="unit-switch-link"
              onClick={handleUnitSwitch}
            >
              Switch to metric
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default WeightInput;
